import React from 'react';
import { Laptop, Shield, Wifi, Clock, Users, Phone, Mail, MapPin, ChevronRight, Award, Building2, Terminal, Database, Cloud, ArrowRight, Bot, Code, Coins, Home, Wrench, Zap } from 'lucide-react';

export function HomePage() {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <div className="hero-container">
        <video 
          autoPlay 
          muted 
          loop 
          playsInline
          className="video-background"
          poster="https://images.unsplash.com/photo-1600607687920-4e2a09cf159d?auto=format&fit=crop&q=80"
        >
          <source src="https://cdn.coverr.co/videos/coverr-computer-repair-shop-5755/1080p.mp4" type="video/mp4" />
        </video>
        <div className="video-overlay"></div>
        <div className="container mx-auto px-4 sm:px-6 relative min-h-screen flex items-center hero-content">
          <div className="max-w-3xl">
            <div className="inline-block bg-mint-600/20 text-mint-400 px-4 py-2 rounded-full text-sm font-medium mb-6">
              Friendly Home Computer Support in Las Vegas
            </div>
            <h1 className="text-4xl sm:text-6xl font-bold text-white mb-6 leading-tight">
              Computer Problems?<br />
              We're Here to <span style={{ color: '#FF6F61' }}>Help!</span>
            </h1>
            <p className="text-lg sm:text-xl text-gray-300 mb-8 sm:mb-12 leading-relaxed">
              From slow computers to WiFi issues, we provide friendly, easy-to-understand tech support right at your home. No geek speak, just clear solutions.
            </p>
            <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4">
              <button 
                onClick={() => {
                  const element = document.getElementById('contact');
                  element?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="bg-mint-600 hover:bg-mint-700 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg text-lg font-semibold flex items-center justify-center group"
              >
                Get Help Now <ArrowRight className="ml-2 transform group-hover:translate-x-1 transition-transform" />
              </button>
              <button 
                onClick={() => {
                  const element = document.getElementById('services');
                  element?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="bg-white/90 hover:bg-white text-mint-900 px-6 sm:px-8 py-3 sm:py-4 rounded-lg text-lg font-semibold transition-colors"
              >
                Our Services
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Services Section */}
      <section id="services" className="py-20 bg-white scroll-mt-24">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Services</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              We offer a comprehensive range of computer repair and IT services to keep your technology running smoothly
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: <Laptop className="w-8 h-8 text-mint-600" />,
                title: "Computer Repair",
                description: "Expert diagnosis and repair for desktop and laptop computers"
              },
              {
                icon: <Wifi className="w-8 h-8 text-mint-600" />,
                title: "Network Setup",
                description: "Professional WiFi and network installation services"
              },
              {
                icon: <Shield className="w-8 h-8 text-mint-600" />,
                title: "Virus Removal",
                description: "Comprehensive malware and virus removal solutions"
              },
              {
                icon: <Database className="w-8 h-8 text-mint-600" />,
                title: "Data Recovery",
                description: "Recovery of lost or corrupted data from any device"
              },
              {
                icon: <Cloud className="w-8 h-8 text-mint-600" />,
                title: "Cloud Services",
                description: "Setup and management of cloud storage and backup"
              },
              {
                icon: <Bot className="w-8 h-8 text-mint-600" />,
                title: "IT Consulting",
                description: "Professional IT advice and strategic planning"
              }
            ].map((service, index) => (
              <div key={index} className="bg-gray-50 rounded-xl p-8 hover:shadow-lg transition-shadow">
                <div className="mb-4">
                  {service.icon}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{service.title}</h3>
                <p className="text-gray-600 mb-4">{service.description}</p>
                <button 
                  onClick={() => {
                    const element = document.getElementById('contact');
                    element?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="text-mint-600 font-medium flex items-center hover:text-mint-700 transition-colors"
                >
                  Learn More <ChevronRight className="ml-1 w-5 h-5" />
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 bg-gray-50 scroll-mt-24">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-4xl font-bold text-gray-900 mb-6">Why Choose Us?</h2>
                <div className="space-y-6">
                  {[
                    {
                      icon: <Award className="w-6 h-6 text-mint-600" />,
                      title: "Certified Experts",
                      description: "Our team consists of certified IT professionals with years of experience"
                    },
                    {
                      icon: <Clock className="w-6 h-6 text-mint-600" />,
                      title: "Fast Response Time",
                      description: "Same-day service available for urgent computer problems"
                    },
                    {
                      icon: <Users className="w-6 h-6 text-mint-600" />,
                      title: "Customer Focused",
                      description: "We pride ourselves on clear communication and customer satisfaction"
                    },
                    {
                      icon: <Building2 className="w-6 h-6 text-mint-600" />,
                      title: "Business & Home Service",
                      description: "Specialized solutions for both residential and commercial clients"
                    }
                  ].map((feature, index) => (
                    <div key={index} className="flex items-start">
                      <div className="flex-shrink-0 mt-1">
                        {feature.icon}
                      </div>
                      <div className="ml-4">
                        <h3 className="text-xl font-semibold text-gray-900 mb-2">{feature.title}</h3>
                        <p className="text-gray-600">{feature.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="relative">
                <img 
                  src="https://images.pexels.com/photos/3183150/pexels-photo-3183150.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2" 
                  alt="IT Professional at work" 
                  className="rounded-xl shadow-lg"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-white scroll-mt-24">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Contact Us</h2>
              <p className="text-xl text-gray-600">
                Get in touch with us for all your computer repair needs
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
              <div className="space-y-8">
                <div className="flex items-center">
                  <Phone className="w-6 h-6 text-mint-600" />
                  <div className="ml-4">
                    <h3 className="text-lg font-semibold text-gray-900">Phone</h3>
                    <p className="text-gray-600">1 (833) HELP-702</p>
                  </div>
                </div>
                
                <div className="flex items-center">
                  <Mail className="w-6 h-6 text-mint-600" />
                  <div className="ml-4">
                    <h3 className="text-lg font-semibold text-gray-900">Email</h3>
                    <p className="text-gray-600">help@lvcomputerhelp.com</p>
                  </div>
                </div>
                
                <div className="flex items-center">
                  <MapPin className="w-6 h-6 text-mint-600" />
                  <div className="ml-4">
                    <h3 className="text-lg font-semibold text-gray-900">Service Area</h3>
                    <p className="text-gray-600">Greater Las Vegas Metropolitan Area</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-gray-50 p-8 rounded-xl">
                <h3 className="text-2xl font-semibold text-gray-900 mb-6">Request Service</h3>
                <form className="space-y-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                    <input
                      type="text"
                      id="name"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mint-500 focus:border-transparent"
                      placeholder="Your name"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                    <input
                      type="email"
                      id="email"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mint-500 focus:border-transparent"
                      placeholder="your@email.com"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                    <textarea
                      id="message"
                      rows={4}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mint-500 focus:border-transparent"
                      placeholder="Describe your computer issue"
                    ></textarea>
                  </div>
                  
                  <button
                    type="submit"
                    className="w-full bg-mint-600 hover:bg-mint-700 text-white px-6 py-3 rounded-lg font-medium transition-colors"
                  >
                    Send Message
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gradient-to-br from-mint-900 via-mint-800 to-mint-900 text-white py-12">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <Terminal className="w-8 h-8 text-mint-400" />
                <span className="text-xl font-bold">
                  Las Vegas <span className="text-mint-400">Computer</span> Help
                </span>
              </div>
              <p className="text-mint-100">
                Professional computer repair services in Las Vegas, NV
              </p>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold mb-4 text-mint-50">Quick Links</h3>
              <ul className="space-y-2">
                <li>
                  <button onClick={() => {
                    const element = document.getElementById('services');
                    element?.scrollIntoView({ behavior: 'smooth' });
                  }} className="text-mint-200 hover:text-white transition-colors">
                    Services
                  </button>
                </li>
                <li>
                  <button onClick={() => {
                    const element = document.getElementById('about');
                    element?.scrollIntoView({ behavior: 'smooth' });
                  }} className="text-mint-200 hover:text-white transition-colors">
                    About Us
                  </button>
                </li>
                <li>
                  <button onClick={() => {
                    const element = document.getElementById('contact');
                    element?.scrollIntoView({ behavior: 'smooth' });
                  }} className="text-mint-200 hover:text-white transition-colors">
                    Contact
                  </button>
                </li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold mb-4 text-mint-50">Services</h3>
              <ul className="space-y-2">
                <li className="text-mint-200">Computer Repair</li>
                <li className="text-mint-200">Virus Removal</li>
                <li className="text-mint-200">Network Setup</li>
                <li className="text-mint-200">Data Recovery</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold mb-4 text-mint-50">Contact Info</h3>
              <ul className="space-y-2">
                <li className="text-mint-200">Phone: 1 (833) HELP-702</li>
                <li className="text-mint-200">Email: help@lvcomputerhelp.com</li>
                <li className="text-mint-200">Las Vegas, NV</li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-mint-700/50 mt-12 pt-8 text-center text-mint-200">
            <p>&copy; {new Date().getFullYear()} Las Vegas Computer Help. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}